import * as React from "react";

import { scaleMediaQuery, usePreferences } from "@/lib/preferences";

/** Breakpoints the app shell is designed around.
 *  - phone:   < 768px    - bottom tab bar, card lists, bottom-sheet dialogs
 *  - tablet:  768–1151px - sidebar collapsed to an icon rail by default
 *  - desktop: ≥ 1152px   - full expanded sidebar
 *  The desktop threshold is deliberately BELOW Tailwind's xl (1280px) - a
 *  maximized 1366px-wide laptop screen still only has ~1272-1300px of
 *  actual content width after browser chrome/scrollbar/OS display scaling,
 *  which used to fall just under 1280 and get treated as "tablet" (sidebar
 *  collapsed) even though it's a normal laptop, not a real tablet or a
 *  narrow window. 1152px keeps genuinely narrow windows and real tablets
 *  (e.g. iPad landscape at 1024px) in tablet mode while covering the
 *  common laptop width.
 *  These widths are at 100% text size - useMediaQuery scales them by the
 *  text-size setting, matching Tailwind's scaled breakpoints. */
export const TABLET_QUERY = "(min-width: 768px) and (max-width: 1151.98px)";
export const PHONE_QUERY = "(max-width: 767.98px)";

/** Live `window.matchMedia` result - re-renders when the query flips, or
 *  when the text-size setting moves its scaled px widths. */
export function useMediaQuery(query: string): boolean {
  const { uiScale } = usePreferences();
  const scaled = scaleMediaQuery(query, uiScale);
  const [matches, setMatches] = React.useState(() =>
    typeof window !== "undefined" ? window.matchMedia(scaled).matches : false
  );

  React.useEffect(() => {
    const mql = window.matchMedia(scaled);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [scaled]);

  return matches;
}
